import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { getLiveChatRatings } from "./supportApi";
import { Star, RefreshCw, MessageSquare } from "lucide-react";

export default function LiveChatFeedback() {
  const [ratings, setRatings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
  });

  // Filters
  const [productFilter, setProductFilter] = useState("all");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date");

  // Fetch ratings
  const fetchRatings = async () => {
    setIsLoading(true);
    try {
      const data = await getLiveChatRatings({ page, limit });
      setRatings(data.ratings || []);
      setPagination({
        total: data.total || 0,
        page: data.page || 1,
        limit: data.limit || 20,
      });
    } catch (error) {
      console.error("Error fetching ratings:", error);
      toast.error("Failed to load feedback");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRatings();
  }, [page]);

  // Filter and sort ratings
  const filteredRatings = useMemo(() => {
    let filtered = [...ratings];

    // Filter by product
    if (productFilter !== "all") {
      filtered = filtered.filter((r) => r.product === productFilter);
    }

    // Filter by rating
    if (ratingFilter !== "all") {
      filtered = filtered.filter((r) => r.rating === parseInt(ratingFilter));
    }

    // Sort
    if (sortBy === "date") {
      filtered.sort((a, b) => new Date(b.closedAt) - new Date(a.closedAt));
    } else if (sortBy === "rating-high") {
      filtered.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === "rating-low") {
      filtered.sort((a, b) => a.rating - b.rating);
    }

    return filtered;
  }, [ratings, productFilter, ratingFilter, sortBy]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format product name
  const formatProduct = (product) => {
    if (!product) return "N/A";
    const productMap = {
      ginox_ai: "Ginox AI",
      gidex: "Gidex",
      ginox_core: "Ginox Core",
      intelligent_x: "Intelligent X",
      platform: "Platform",
      signal_x: "Signal X",
    };
    return productMap[product] || product;
  };

  // Render star rating
  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            aria-hidden="true"
            className={`h-3.5 w-3.5 ${
              star <= rating
                ? "fill-yellow-400 text-yellow-400"
                : "text-muted-foreground/30"
            }`}
          />
        ))}
      </div>
    );
  };

  // Get rating badge classes
  const getRatingBadgeClass = (rating) => {
    if (rating >= 4)
      return "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-400/10 text-emerald-400";
    if (rating === 3)
      return "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-400/10 text-yellow-400";
    return "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-400/10 text-red-400";
  };

  // Stats
  const stats = useMemo(() => {
    const total = ratings.length;
    const avgRating =
      total > 0
        ? (ratings.reduce((sum, r) => sum + r.rating, 0) / total).toFixed(1)
        : 0;
    const withFeedback = ratings.filter((r) => r.feedback).length;
    const byCustomer = ratings.filter((r) => r.closedBy === "customer").length;
    const byAgent = ratings.filter((r) => r.closedBy === "agent").length;

    return { total, avgRating, withFeedback, byCustomer, byAgent };
  }, [ratings]);

  const selectClass =
    "rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30 w-full md:w-48";

  return (
    <div className="">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
              <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
              Support
            </div>
            <h1 className="font-serif italic text-[36px] leading-[1.05] text-foreground mb-1">
              LiveChat <span className="text-gradient-ginox">Feedback</span>
            </h1>
            <p className="text-sm text-muted-foreground mb-4">
              View customer ratings and feedback from live chat sessions
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="rounded-lg border border-border/70 bg-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-3">
              Total Ratings
            </p>
            <p className="text-2xl font-mono text-foreground">{stats.total}</p>
          </div>

          <div className="rounded-lg border border-border/70 bg-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-3">
              Avg Rating
            </p>
            <p className="text-2xl font-mono text-foreground">{stats.avgRating}</p>
          </div>

          <div className="rounded-lg border border-border/70 bg-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-3">
              With Feedback
            </p>
            <p className="text-2xl font-mono text-foreground">{stats.withFeedback}</p>
          </div>

          <div className="rounded-lg border border-border/70 bg-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-3">
              By Customer
            </p>
            <p className="text-2xl font-mono text-foreground">{stats.byCustomer}</p>
          </div>

          <div className="rounded-lg border border-border/70 bg-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-3">
              By Agent
            </p>
            <p className="text-2xl font-mono text-foreground">{stats.byAgent}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <select
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            className={selectClass}
          >
            <option value="all">All Products</option>
            <option value="ginox_ai">Ginox AI</option>
            <option value="gidex">Gidex</option>
            <option value="ginox_core">Ginox Core</option>
            <option value="intelligent_x">Intelligent X</option>
            <option value="platform">Platform</option>
            <option value="signal_x">Signal X</option>
          </select>

          <select
            value={ratingFilter}
            onChange={(e) => setRatingFilter(e.target.value)}
            className={selectClass}
          >
            <option value="all">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className={selectClass}
          >
            <option value="date">Date (Newest First)</option>
            <option value="rating-high">Rating (High to Low)</option>
            <option value="rating-low">Rating (Low to High)</option>
          </select>

          <button
            onClick={fetchRatings}
            disabled={isLoading}
            className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
              isLoading
                ? "border-border/40 bg-white/[0.02] text-muted-foreground cursor-not-allowed"
                : "border-ginox-green/60 bg-ginox-green/10 text-ginox-green hover:bg-ginox-green/20"
            }`}
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`}
              aria-hidden="true"
            />
            {isLoading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {/* Feedback Table */}
        <div className="rounded-lg border border-border/70 bg-card p-5">
          <div className="mb-4">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-1">
              Feedback List
            </p>
            <p className="text-xs text-muted-foreground">
              Showing {filteredRatings.length} of {pagination.total} ratings
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green mx-auto" />
            </div>
          ) : filteredRatings.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">
              No feedback found
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Customer
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Email
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Product
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Rating
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Feedback
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Closed By
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRatings.map((rating) => (
                    <tr
                      key={rating.id}
                      className="hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="border-b border-border/40 px-4 py-3 text-sm text-foreground">
                        {rating.customerName || "N/A"}
                      </td>
                      <td className="border-b border-border/40 px-4 py-3 text-sm text-foreground font-mono break-all">
                        {rating.customerEmail || "N/A"}
                      </td>
                      <td className="border-b border-border/40 px-4 py-3 text-sm text-foreground">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-ginox-green/10 text-ginox-green">
                          {formatProduct(rating.product)}
                        </span>
                      </td>
                      <td className="border-b border-border/40 px-4 py-3">
                        <div className="flex items-center gap-2">
                          {renderStars(rating.rating)}
                          <span className={getRatingBadgeClass(rating.rating)}>
                            {rating.rating}
                          </span>
                        </div>
                      </td>
                      <td className="border-b border-border/40 px-4 py-3 text-sm text-foreground max-w-xs">
                        {rating.feedback ? (
                          <span className="line-clamp-2">{rating.feedback}</span>
                        ) : (
                          <span className="text-muted-foreground/50 italic text-xs">
                            No feedback
                          </span>
                        )}
                      </td>
                      <td className="border-b border-border/40 px-4 py-3 text-sm text-foreground">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            rating.closedBy === "customer"
                              ? "bg-emerald-400/10 text-emerald-400"
                              : "bg-ginox-green/10 text-ginox-green"
                          }`}
                        >
                          {rating.closedBy}
                        </span>
                      </td>
                      <td className="border-b border-border/40 px-4 py-3 text-sm text-foreground font-mono whitespace-nowrap">
                        {formatDate(rating.closedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.total > limit && (
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground font-mono">
              Showing {filteredRatings.length} of {pagination.total} ratings
              (Page {pagination.page} of {Math.ceil(pagination.total / limit)})
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
                  page === 1
                    ? "border-border/40 bg-white/[0.02] text-muted-foreground/40 cursor-not-allowed"
                    : "border-border/70 bg-white/[0.03] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
                }`}
              >
                First
              </button>
              <button
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page === 1}
                className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
                  page === 1
                    ? "border-border/40 bg-white/[0.02] text-muted-foreground/40 cursor-not-allowed"
                    : "border-border/70 bg-white/[0.03] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
                }`}
              >
                Previous
              </button>
              <span className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green font-mono">
                {page}
              </span>
              <button
                onClick={() =>
                  setPage((prev) =>
                    Math.min(Math.ceil(pagination.total / limit), prev + 1),
                  )
                }
                disabled={page >= Math.ceil(pagination.total / limit)}
                className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
                  page >= Math.ceil(pagination.total / limit)
                    ? "border-border/40 bg-white/[0.02] text-muted-foreground/40 cursor-not-allowed"
                    : "border-border/70 bg-white/[0.03] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
                }`}
              >
                Next
              </button>
              <button
                onClick={() => setPage(Math.ceil(pagination.total / limit))}
                disabled={page >= Math.ceil(pagination.total / limit)}
                className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
                  page >= Math.ceil(pagination.total / limit)
                    ? "border-border/40 bg-white/[0.02] text-muted-foreground/40 cursor-not-allowed"
                    : "border-border/70 bg-white/[0.03] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
                }`}
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
